import { useState } from "react";
import { Plus, Trash2, Upload, X, FileJson } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useBulkInsertSightings, type NewSightingLocation } from "@/hooks/useSightingLocations";
import { toast } from "@/hooks/use-toast";

const emptyRow = (): NewSightingLocation => ({
  id: "",
  lat: 57.3,
  lng: -4.45,
  intensity: 0.5,
  label: "",
  year: "",
  description: "",
  witness: "",
});

interface BulkSightingImportProps {
  open: boolean;
  onClose: () => void;
}

const BulkSightingImport = ({ open, onClose }: BulkSightingImportProps) => {
  const [mode, setMode] = useState<"form" | "json">("form");
  const [rows, setRows] = useState<NewSightingLocation[]>([emptyRow()]);
  const [jsonText, setJsonText] = useState("");
  const [errors, setErrors] = useState<string[]>([]);
  const bulkInsert = useBulkInsertSightings();

  if (!open) return null;

  const updateRow = (idx: number, field: keyof NewSightingLocation, value: string | number) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);
  const removeRow = (idx: number) => setRows((prev) => prev.filter((_, i) => i !== idx));

  const validate = (locations: NewSightingLocation[]): string[] => {
    const errs: string[] = [];
    locations.forEach((loc, i) => {
      if (!loc.id.trim()) errs.push(`Row ${i + 1}: ID is required`);
      if (!loc.label.trim()) errs.push(`Row ${i + 1}: Label is required`);
      if (!loc.year.trim()) errs.push(`Row ${i + 1}: Year is required`);
      if (!loc.description.trim()) errs.push(`Row ${i + 1}: Description is required`);
      if (loc.lat < -90 || loc.lat > 90) errs.push(`Row ${i + 1}: Invalid latitude`);
      if (loc.lng < -180 || loc.lng > 180) errs.push(`Row ${i + 1}: Invalid longitude`);
      if (loc.intensity < 0 || loc.intensity > 1) errs.push(`Row ${i + 1}: Intensity must be 0–1`);
    });
    return errs;
  };

  const handleSubmitForm = async () => {
    const errs = validate(rows);
    setErrors(errs);
    if (errs.length > 0) return;

    const cleaned = rows.map((r) => ({
      ...r,
      witness: r.witness?.trim() || null,
    }));

    try {
      await bulkInsert.mutateAsync(cleaned);
      toast({ title: "Success", description: `${cleaned.length} sighting(s) added to the map.` });
      setRows([emptyRow()]);
      onClose();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const handleSubmitJson = async () => {
    try {
      const parsed = JSON.parse(jsonText);
      const arr: NewSightingLocation[] = Array.isArray(parsed) ? parsed : [parsed];
      const errs = validate(arr);
      setErrors(errs);
      if (errs.length > 0) return;

      const cleaned = arr.map((r) => ({
        ...r,
        witness: r.witness?.trim() || null,
      }));

      await bulkInsert.mutateAsync(cleaned);
      toast({ title: "Success", description: `${cleaned.length} sighting(s) added from JSON.` });
      setJsonText("");
      onClose();
    } catch (e: any) {
      if (e instanceof SyntaxError) {
        setErrors(["Invalid JSON format. Please check your input."]);
      } else {
        toast({ title: "Error", description: e.message, variant: "destructive" });
      }
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-4">
      <div className="bg-card border border-border rounded-xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="font-serif text-lg font-bold text-foreground">Bulk Add Sightings</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Mode tabs */}
        <div className="flex gap-2 p-4 pb-0">
          <Button
            variant={mode === "form" ? "default" : "outline"}
            size="sm"
            onClick={() => setMode("form")}
          >
            <Plus className="h-3 w-3 mr-1" /> Form
          </Button>
          <Button
            variant={mode === "json" ? "default" : "outline"}
            size="sm"
            onClick={() => setMode("json")}
          >
            <FileJson className="h-3 w-3 mr-1" /> Paste JSON
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {errors.length > 0 && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3">
              {errors.map((e, i) => (
                <p key={i} className="text-destructive text-xs">{e}</p>
              ))}
            </div>
          )}

          {mode === "form" ? (
            <>
              {rows.map((row, idx) => (
                <div key={idx} className="border border-border rounded-lg p-3 space-y-2 bg-background/50">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">Sighting {idx + 1}</span>
                    {rows.length > 1 && (
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeRow(idx)}>
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    <Input placeholder="ID (slug)" value={row.id} onChange={(e) => updateRow(idx, "id", (e.target as HTMLInputElement).value)} className="text-xs" />
                    <Input placeholder="Label" value={row.label} onChange={(e) => updateRow(idx, "label", (e.target as HTMLInputElement).value)} className="text-xs" />
                    <Input placeholder="Year" value={row.year} onChange={(e) => updateRow(idx, "year", (e.target as HTMLInputElement).value)} className="text-xs" />
                    <Input type="number" step="0.0001" placeholder="Lat" value={row.lat} onChange={(e) => updateRow(idx, "lat", parseFloat((e.target as HTMLInputElement).value) || 0)} className="text-xs" />
                    <Input type="number" step="0.0001" placeholder="Lng" value={row.lng} onChange={(e) => updateRow(idx, "lng", parseFloat((e.target as HTMLInputElement).value) || 0)} className="text-xs" />
                    <Input type="number" step="0.1" min="0" max="1" placeholder="Intensity (0-1)" value={row.intensity} onChange={(e) => updateRow(idx, "intensity", parseFloat((e.target as HTMLInputElement).value) || 0)} className="text-xs" />
                    <Input placeholder="Witness (optional)" value={row.witness || ""} onChange={(e) => updateRow(idx, "witness", (e.target as HTMLInputElement).value)} className="text-xs col-span-2 md:col-span-3" />
                  </div>
                  <Textarea placeholder="Description" value={row.description} onChange={(e) => updateRow(idx, "description", e.target.value)} className="text-xs min-h-[60px]" />
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addRow} className="w-full">
                <Plus className="h-3 w-3 mr-1" /> Add another sighting
              </Button>
            </>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                Paste a JSON array of sighting objects. Each needs: <code className="text-accent">id, lat, lng, intensity, label, year, description</code>. Optional: <code className="text-accent">witness</code>.
              </p>
              <Textarea
                value={jsonText}
                onChange={(e) => setJsonText(e.target.value)}
                placeholder={`[{\n  "id": "my-sighting",\n  "lat": 57.32,\n  "lng": -4.44,\n  "intensity": 0.6,\n  "label": "My Sighting",\n  "year": "1990",\n  "description": "A description..."\n}]`}
                className="min-h-[200px] font-mono text-xs"
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-border">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={mode === "form" ? handleSubmitForm : handleSubmitJson}
            disabled={bulkInsert.isPending}
          >
            <Upload className="h-3 w-3 mr-1" />
            {bulkInsert.isPending ? "Saving…" : `Add Sighting${mode === "form" && rows.length > 1 ? "s" : ""}`}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BulkSightingImport;
